'use client';

import { createContext, use, useEffect, useMemo, useState, useCallback } from 'react';
import { useSession } from './AuthContext';
import { useOrgStore } from '@/stores/orgStore';
import {
  useOrganizationsQuery,
  useUpdateOrganizationMutation,
} from '@/hooks/api/useOrganizations';
import {
  listMyInvitations,
  useAcceptInvitationMutation,
  useInviteOrganizationMemberMutation,
} from '@/hooks/api/useOrganizationMembers';
import type {
  Organization,
  OrganizationMember,
  OrganizationInvitation,
  OrganizationContextType,
  UserProfile,
  OrganizationRole,
} from '@/types/organizations';
import { ORGANIZATION_PERMISSIONS } from '@/types/organizations';

export const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = use(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

type Props = { children: React.ReactNode };

type OrganizationApiItem = Organization & { role?: OrganizationRole };

export const OrganizationProvider = ({ children }: Props) => {
  const { session } = useSession();
  const currentOrganizationId = useOrgStore((state) => state.currentOrganizationId);
  const setCurrentOrganizationId = useOrgStore((state) => state.setCurrentOrganizationId);
  const [isSwitching, setIsSwitching] = useState(false);

  const organizationsQuery = useOrganizationsQuery(!!session?.user?.id);
  const isSuperAdmin = session?.user?.is_super_admin ?? false;
  const updateOrganizationMutation = useUpdateOrganizationMutation();
  const acceptInvitationMutation = useAcceptInvitationMutation();

  const organizations = useMemo(
    () => (organizationsQuery.data ?? []) as OrganizationApiItem[],
    [organizationsQuery.data],
  );

  const currentOrganization = useMemo(
    () => organizations.find((org) => org.id === currentOrganizationId) ?? null,
    [organizations, currentOrganizationId],
  );

  const inviteMemberMutation = useInviteOrganizationMemberMutation(currentOrganizationId ?? undefined);

  const userMemberships = useMemo<OrganizationMember[]>(() => {
    if (!session?.user?.id) return [];
    return organizations
      .filter((organization) => !!organization.role)
      .map((organization) => ({
        id: `${organization.id}:${session.user.id}`,
        organization_id: organization.id,
        user_id: session.user.id,
        role: organization.role as OrganizationRole,
        joined_at: organization.created_at,
        organization,
      }));
  }, [organizations, session?.user?.id]);

  useEffect(() => {
    if (!session?.user?.id) {
      setCurrentOrganizationId(null);
      return;
    }

    if (organizations.length === 0) {
      setCurrentOrganizationId(null);
      return;
    }

    const selected =
      organizations.find((org) => org.id === currentOrganizationId) || organizations[0];

    if (currentOrganizationId !== selected.id) {
      setCurrentOrganizationId(selected.id);
    }
  }, [organizations, session?.user?.id, currentOrganizationId, setCurrentOrganizationId]);

  const switchOrganization = useCallback(async (organizationId: string) => {
    setIsSwitching(true);
    try {
      const organization = organizations.find((org) => org.id === organizationId);
      if (!organization) {
        throw new Error(`Organization ${organizationId} not found in available organizations`);
      }
      setCurrentOrganizationId(organization.id);
    } finally {
      setIsSwitching(false);
    }
  }, [organizations, setCurrentOrganizationId]);

  const acceptInvitation = useCallback(async (invitationId: string): Promise<void> => {
    const invitations = await listMyInvitations();
    const invitation = invitations.find((item) => item.id === invitationId);
    if (!invitation) {
      throw new Error('Invitación no encontrada o ya procesada');
    }

    await acceptInvitationMutation.mutateAsync({
      orgId: invitation.organization_id,
      invitationId: invitation.id,
    });
    await organizationsQuery.refetch();
  }, [acceptInvitationMutation, organizationsQuery]);

  const updateOrganization = useCallback(
    async (id: string, data: Partial<Organization>): Promise<Organization> => {
      return updateOrganizationMutation.mutateAsync({
        orgId: id,
        payload: data,
      });
    },
    [updateOrganizationMutation],
  );

  const inviteUser = useCallback(
    async (email: string, role: OrganizationRole): Promise<OrganizationInvitation> => {
      if (!currentOrganization) {
        throw new Error('Organización actual no disponible');
      }
      if (role === 'owner') {
        throw new Error('No se puede invitar con rol owner');
      }
      return inviteMemberMutation.mutateAsync({
        email: email.trim().toLowerCase(),
        role,
      });
    },
    [currentOrganization, inviteMemberMutation],
  );

  const getUserProfile = useCallback(async (): Promise<UserProfile | null> => {
    if (!session?.user?.id) return null;
    return {
      id: session.user.id,
      email: session.user.email,
      full_name: session.user.name ?? undefined,
      avatar_url: session.user.avatar_url ?? undefined,
      created_at: '',
      updated_at: '',
    };
  }, [session?.user]);

  const hasPermission = useCallback(
    (action: string): boolean => {
      if (!currentOrganization || !session?.user?.id) return false;
      if (isSuperAdmin) return true;

      const membership = userMemberships.find(
        (member) =>
          member.organization_id === currentOrganization.id && member.user_id === session.user.id,
      );
      if (!membership) return false;

      const permissions = ORGANIZATION_PERMISSIONS[membership.role];
      return permissions.includes('*') || permissions.includes(action);
    },
    [currentOrganization, session?.user?.id, userMemberships, isSuperAdmin],
  );

  const value: OrganizationContextType = {
    currentOrganization,
    organizations,
    userMemberships,
    isLoading: organizationsQuery.isLoading || isSwitching,
    switchOrganization,
    acceptInvitation,
    updateOrganization,
    inviteUser,
    getUserProfile,
    hasPermission,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};
